/**
 * Feature 5: Brand Popularity Trends
 * Shows time-series data for brand performance metrics
 */

import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import {
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  Button,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import { TrendingUp } from '@mui/icons-material';
import apiService from '../services/apiService';

const POPULAR_BRANDS = ['samsung', 'apple', 'xiaomi', 'huawei', 'lg', 'sony'];

const BrandTrends = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState('samsung');
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (selectedBrand) {
      fetchData();
    }
  }, [selectedBrand]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getBrandTrends(selectedBrand);
      setData(response);
    } catch (err) {
      setError(`Failed to load data for brand "${selectedBrand}"`);
      console.error(err);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatCurrency = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  const calculateTotals = (trends) => {
    if (!trends || trends.length === 0) return null;
    
    return {
      totalViews: trends.reduce((sum, d) => sum + d.views, 0),
      totalCarts: trends.reduce((sum, d) => sum + d.carts, 0),
      totalPurchases: trends.reduce((sum, d) => sum + d.purchases, 0),
      totalRevenue: trends.reduce((sum, d) => sum + d.revenue, 0),
      avgDailyViews: trends.reduce((sum, d) => sum + d.views, 0) / trends.length,
      avgDailyRevenue: trends.reduce((sum, d) => sum + d.revenue, 0) / trends.length,
    };
  };

  const totals = data?.trends ? calculateTotals(data.trends) : null;

  return (
    <Card>
      <CardContent>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <Typography variant="h5" gutterBottom>
              Brand Popularity Trends
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Daily performance metrics for {data?.brand || 'selected brand'}
            </Typography>
          </div>
          
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <TextField
              select
              label="Select Brand"
              value={selectedBrand}
              onChange={(e) => setSelectedBrand(e.target.value)}
              size="small"
              style={{ minWidth: '150px' }}
            >
              {POPULAR_BRANDS.map((brand) => (
                <MenuItem key={brand} value={brand}>
                  {brand.charAt(0).toUpperCase() + brand.slice(1)}
                </MenuItem>
              ))}
            </TextField>
            <Button variant="outlined" size="small" onClick={fetchData}>
              Refresh
            </Button>
          </div>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <CircularProgress />
            <Typography variant="body2" style={{ marginTop: '16px' }}>
              Loading trends for {selectedBrand}...
            </Typography>
          </div>
        )}

        {error && <Alert severity="error" style={{ marginBottom: '16px' }}>{error}</Alert>}

        {!loading && data && data.trends && (
          <>
            {/* Summary Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '24px' }}>
              <div style={{ padding: '12px', backgroundColor: '#e3f2fd', borderRadius: '8px' }}>
                <Typography variant="caption" color="textSecondary">
                  Total Views
                </Typography>
                <Typography variant="h6">{formatNumber(totals.totalViews)}</Typography>
                <Typography variant="caption">
                  Avg: {formatNumber(Math.round(totals.avgDailyViews))}/day
                </Typography>
              </div>
              
              <div style={{ padding: '12px', backgroundColor: '#fff3e0', borderRadius: '8px' }}>
                <Typography variant="caption" color="textSecondary">
                  Total Carts
                </Typography>
                <Typography variant="h6">{formatNumber(totals.totalCarts)}</Typography>
              </div>
              
              <div style={{ padding: '12px', backgroundColor: '#e8f5e9', borderRadius: '8px' }}>
                <Typography variant="caption" color="textSecondary">
                  Total Purchases
                </Typography>
                <Typography variant="h6">{formatNumber(totals.totalPurchases)}</Typography>
              </div>
              
              <div style={{ padding: '12px', backgroundColor: '#f3e5f5', borderRadius: '8px' }}>
                <Typography variant="caption" color="textSecondary">
                  Total Revenue
                </Typography>
                <Typography variant="h6">{formatCurrency(totals.totalRevenue)}</Typography>
                <Typography variant="caption">
                  Avg: {formatCurrency(totals.avgDailyRevenue)}/day
                </Typography>
              </div>
            </div>

            {/* Date Range Info */}
            <div style={{ marginBottom: '16px', display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Chip
                icon={<TrendingUp />}
                label={`${data.total_records} days of data`}
                size="small"
              />
              <Chip
                label={`${data.date_range.start} to ${data.date_range.end}`}
                variant="outlined"
                size="small"
              />
            </div>

            {/* Chart Tabs */}
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} style={{ marginBottom: '16px' }}>
              <Tab label="User Engagement" />
              <Tab label="Revenue Trends" />
              <Tab label="All Metrics" />
            </Tabs>

            {/* User Engagement Chart */}
            {activeTab === 0 && (
              <ResponsiveContainer width="100%" height={400}>
                <LineChart
                  data={data.trends}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={formatDate} />
                  <YAxis tickFormatter={(value) => formatNumber(value)} />
                  <Tooltip
                    labelFormatter={(label) => new Date(label).toLocaleDateString()}
                    formatter={(value) => formatNumber(value)}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="views" stroke="#2196F3" strokeWidth={2} dot={false} name="Views" />
                  <Line type="monotone" dataKey="carts" stroke="#FF9800" strokeWidth={2} dot={false} name="Carts" />
                  <Line type="monotone" dataKey="purchases" stroke="#4CAF50" strokeWidth={2} dot={false} name="Purchases" />
                </LineChart>
              </ResponsiveContainer>
            )}

            {/* Revenue Trends Chart */}
            {activeTab === 1 && (
              <ResponsiveContainer width="100%" height={400}>
                <AreaChart
                  data={data.trends}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={formatDate} />
                  <YAxis tickFormatter={formatCurrency} />
                  <Tooltip
                    labelFormatter={(label) => new Date(label).toLocaleDateString()}
                    formatter={(value) => formatCurrency(value)}
                  />
                  <Legend />
                  <Area type="monotone" dataKey="revenue" stroke="#9C27B0" fill="#9C27B0" fillOpacity={0.6} name="Revenue" />
                </AreaChart>
              </ResponsiveContainer>
            )}

            {/* All Metrics Chart */}
            {activeTab === 2 && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={data.trends} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tickFormatter={formatDate} />
                    <YAxis />
                    <Tooltip labelFormatter={(label) => new Date(label).toLocaleDateString()} />
                    <Legend />
                    <Line type="monotone" dataKey="views" stroke="#2196F3" dot={false} name="Views" />
                  </LineChart>
                </ResponsiveContainer>

                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={data.trends} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tickFormatter={formatDate} />
                    <YAxis />
                    <Tooltip labelFormatter={(label) => new Date(label).toLocaleDateString()} />
                    <Legend />
                    <Line type="monotone" dataKey="purchases" stroke="#4CAF50" dot={false} name="Purchases" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Conversion Metrics */}
            <div style={{ marginTop: '20px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
              <Typography variant="subtitle2" gutterBottom>
                📈 Conversion Metrics
              </Typography>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                <div>
                  <Typography variant="caption">View to Cart Rate</Typography>
                  <Typography variant="body1">
                    {((totals.totalCarts / totals.totalViews) * 100).toFixed(2)}%
                  </Typography>
                </div>
                <div>
                  <Typography variant="caption">Cart to Purchase Rate</Typography>
                  <Typography variant="body1">
                    {((totals.totalPurchases / totals.totalCarts) * 100).toFixed(2)}%
                  </Typography>
                </div>
                <div>
                  <Typography variant="caption">Overall Conversion</Typography>
                  <Typography variant="body1">
                    {((totals.totalPurchases / totals.totalViews) * 100).toFixed(2)}%
                  </Typography>
                </div>
                <div>
                  <Typography variant="caption">Avg Revenue per Purchase</Typography>
                  <Typography variant="body1">
                    {formatCurrency(totals.totalRevenue / totals.totalPurchases)}
                  </Typography>
                </div>
              </div>
            </div>
          </>
        )}

        {!loading && !data && !error && (
          <Alert severity="info">Select a brand to view trends</Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default BrandTrends;
